import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { hashPassword } from '@/utils/crypto';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BrandMark } from '@/components/BrandLogo';
import UnpaidDepositsDialog from '@/components/UnpaidDepositsDialog';
import {
  Loader2,
  LogOut,
  Building2,
  Upload,
  Mail,
  LayoutDashboard,
  BarChart3,
  CreditCard,
  ShieldCheck,
  RotateCcw,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const brandUrl = (file) => `${process.env.PUBLIC_URL || ''}/brand/${file}`;

function stripeStatusLabel(stripe) {
  if (!stripe) return 'Not started';
  if (stripe.charges_enabled) return 'Live';
  if (stripe.connected) return 'Setup';
  return 'Not connected';
}

function stripeStatusVariant(stripe) {
  if (!stripe) return 'secondary';
  if (stripe.charges_enabled) return 'default';
  if (stripe.connected) return 'secondary';
  return 'outline';
}

function formatGbp(amount) {
  const value = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  return `£${value.toFixed(2)}`;
}

export default function PlatformDashboard() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    restaurant_count: 0,
    booking_count: 0,
    emails_sent: 0,
  });
  const [securityMetrics, setSecurityMetrics] = useState({
    window_hours: 24,
    event_counts: {},
    active_lockouts: {},
    top_ips: [],
  });
  const [activeLockouts, setActiveLockouts] = useState([]);
  const [unlockingLockoutKey, setUnlockingLockoutKey] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [filter, setFilter] = useState('all');
  const [unpaidDepositsFor, setUnpaidDepositsFor] = useState(null);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingStripeFor, setSendingStripeFor] = useState(null);
  const [sendingBillingFor, setSendingBillingFor] = useState(null);
  const [billingOverrideDialog, setBillingOverrideDialog] = useState(null); // { restaurant }
  const [billingOverrideNote, setBillingOverrideNote] = useState('');
  const [savingBillingOverride, setSavingBillingOverride] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    owner_name: '',
    owner_email: '',
    owner_password: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const [res, secRes, lockoutsRes] = await Promise.all([
        axios.get(`${API}/platform/restaurants`, { params }),
        axios.get(`${API}/platform/security-metrics`, { params: { hours: 24 } }).catch(() => ({ data: null })),
        axios.get(`${API}/platform/security-lockouts`).catch(() => ({ data: { items: [] } })),
      ]);
      setSummary(
        res.data.summary || {
          restaurant_count: 0,
          booking_count: 0,
          emails_sent: 0,
        }
      );
      setRestaurants(res.data.restaurants || []);
      if (secRes?.data) {
        setSecurityMetrics(secRes.data);
      }
      setActiveLockouts(lockoutsRes?.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/platform/restaurants/${id}`, { status });
      toast.success(status === 'suspended' ? 'Restaurant suspended' : 'Restaurant activated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const sendStripeSetupEmail = async (restaurantId) => {
    setSendingStripeFor(restaurantId);
    try {
      const res = await axios.post(`${API}/platform/restaurants/${restaurantId}/send-stripe-setup-email`);
      toast.success(`Stripe setup guide sent to ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingStripeFor(null);
    }
  };

  const startBillingCheckout = async (restaurantId) => {
    setSendingBillingFor(restaurantId);
    try {
      const res = await axios.post(`${API}/platform/restaurants/${restaurantId}/subscription-checkout`, {
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.open(res.data.checkout_url, '_blank', 'noopener,noreferrer');
        toast.success('Opened Stripe subscription checkout');
      } else {
        toast.error('No checkout URL returned');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start billing checkout');
    } finally {
      setSendingBillingFor(null);
    }
  };

  const grantFullAccess = async () => {
    if (!billingOverrideDialog) return;
    setSavingBillingOverride(true);
    try {
      await axios.patch(`${API}/platform/restaurants/${billingOverrideDialog.restaurant.id}/billing`, {
        subscription_status: 'active',
        note: billingOverrideNote.trim() || 'Granted by platform admin',
      });
      toast.success(`Full access granted to ${billingOverrideDialog.restaurant.name}`);
      setBillingOverrideDialog(null);
      setBillingOverrideNote('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update billing');
    } finally {
      setSavingBillingOverride(false);
    }
  };

  const resetToTrial = async (restaurant) => {
    try {
      await axios.patch(`${API}/platform/restaurants/${restaurant.id}/billing`, {
        subscription_status: 'trialing',
        note: 'Reset to trial by platform admin',
      });
      toast.success(`${restaurant.name} reset to trial`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset billing');
    }
  };

  const clearLockout = async (kind, key) => {
    const unlockKey = `${kind}:${key}`;
    setUnlockingLockoutKey(unlockKey);
    try {
      await axios.post(`${API}/platform/security-lockouts/unlock`, { kind, key });
      toast.success('Lockout cleared');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear lockout');
    } finally {
      setUnlockingLockoutKey(null);
    }
  };

  const handleProvision = async (e) => {
    e.preventDefault();
    if (!form.owner_password || form.owner_password.length < 6) {
      toast.error('Owner password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      const owner_password = await hashPassword(form.owner_password);
      await axios.post(`${API}/platform/restaurants`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim().toLowerCase(),
        owner_password,
      });
      toast.success('Restaurant and owner account created');
      setForm({
        name: '',
        email: '',
        phone: '',
        address: '',
        owner_name: '',
        owner_email: '',
        owner_password: '',
      });
      setProvisionOpen(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to create tenant';
      console.error('Create tenant error:', err.response?.status, msg);
      toast.error(msg, { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandMark size="md" className="rounded-lg" />
            <div>
              <h1 className="font-heading text-lg font-bold">DineSpot Platform</h1>
              <p className="text-xs text-muted-foreground">Signed in as {user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" asChild className="gap-2">
              <Link to="/platform/import">
                <Upload className="h-4 w-4" />
                Import CSV
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={logout} className="gap-2" data-testid="platform-logout">
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-8 px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Overview</p>
            <h2 className="font-heading text-2xl font-bold tracking-tight">How the platform is doing</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Totals across all venues you manage. Use the table to see each restaurant&apos;s Google Calendar and
              Stripe deposit status, and to email Stripe setup instructions to their company address.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Card className="border-border/60 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Restaurants</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold">{summary.restaurant_count}</p>
              <p className="text-xs text-muted-foreground mt-1">Tenants on the platform</p>
            </CardContent>
          </Card>
          <Card className="border-border/60 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Bookings</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold">{summary.booking_count}</p>
              <p className="text-xs text-muted-foreground mt-1">All-time booking records</p>
            </CardContent>
          </Card>
          <Card className="border-border/60 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Emails sent</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold">{summary.emails_sent}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Confirmations, deposit receipts, and reminders marked as sent on bookings
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-border/60 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading">Active lockouts</CardTitle>
            <CardDescription>
              Platform admin can manually clear active login or PIN lockouts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeLockouts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No active lockouts right now.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border/60">
                <table className="w-full min-w-[760px] text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-muted-foreground">
                      <th className="px-3 py-2.5 font-medium">Type</th>
                      <th className="px-3 py-2.5 font-medium">Email / Restaurant</th>
                      <th className="px-3 py-2.5 font-medium">IP</th>
                      <th className="px-3 py-2.5 font-medium">Remaining</th>
                      <th className="px-3 py-2.5 font-medium">Until</th>
                      <th className="px-3 py-2.5 text-right font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeLockouts.map((l) => {
                      const rowKey = `${l.kind}:${l.key}`;
                      const minsRemaining = Math.ceil((Number(l.seconds_remaining) || 0) / 60);
                      return (
                        <tr key={rowKey} className="border-b border-border/50 last:border-0">
                          <td className="px-3 py-2.5">
                            <Badge variant="outline">{l.kind === 'login_lockout' ? 'Login' : 'PIN'}</Badge>
                          </td>
                          <td className="px-3 py-2.5">
                            {l.email || l.restaurant_id || '—'}
                          </td>
                          <td className="px-3 py-2.5 font-mono text-xs">{l.ip || '—'}</td>
                          <td className="px-3 py-2.5">{minsRemaining} min</td>
                          <td className="px-3 py-2.5 text-xs text-muted-foreground">
                            {l.locked_until
                              ? (() => {
                                  try {
                                    return format(new Date(l.locked_until), 'PPp');
                                  } catch {
                                    return l.locked_until;
                                  }
                                })()
                              : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={unlockingLockoutKey === rowKey}
                              onClick={() => clearLockout(l.kind, l.key)}
                            >
                              {unlockingLockoutKey === rowKey ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Allow back in'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 sm:grid-cols-3">
          <Card className="border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Security (24h)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold">{securityMetrics.event_counts?.rate_limit_blocked || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Requests blocked by auth rate limiting</p>
            </CardContent>
          </Card>
          <Card className="border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Login lockouts (active)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold">{securityMetrics.active_lockouts?.login_lockout || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">IPs currently locked from password login</p>
            </CardContent>
          </Card>
          <Card className="border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">PIN lockouts (active)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold">{securityMetrics.active_lockouts?.pin_lockout || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">IP+restaurant lockouts currently in effect</p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-border/60 shadow-sm">
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 font-heading">
                <LayoutDashboard className="h-5 w-5" />
                Restaurants & integrations
              </CardTitle>
              <CardDescription>
                Suspending blocks the public booking widget and staff login for that venue. Send the Stripe setup email
                only from here — it goes to the venue&apos;s company email on file.
              </CardDescription>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setProvisionOpen((o) => !o)}>
              {provisionOpen ? 'Hide form' : 'Add restaurant'}
            </Button>
          </CardHeader>
          {provisionOpen && (
            <CardContent className="border-t pt-6">
              <form onSubmit={handleProvision} className="grid max-w-xl gap-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="pv-name">Restaurant name</Label>
                    <Input
                      id="pv-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pv-email">Restaurant email</Label>
                    <Input
                      id="pv-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="pv-phone">Phone</Label>
                    <Input
                      id="pv-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="pv-address">Address</Label>
                    <Input
                      id="pv-address"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <p className="text-sm font-medium text-foreground">First admin (venue owner)</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="pv-oname">Name</Label>
                    <Input
                      id="pv-oname"
                      value={form.owner_name}
                      onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pv-oemail">Email</Label>
                    <Input
                      id="pv-oemail"
                      type="email"
                      value={form.owner_email}
                      onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="pv-opw">Password</Label>
                    <Input
                      id="pv-opw"
                      type="password"
                      autoComplete="new-password"
                      value={form.owner_password}
                      onChange={(e) => setForm({ ...form, owner_password: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                </div>
                <Button type="submit" disabled={saving} className="w-fit">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create tenant'}
                </Button>
              </form>
            </CardContent>
          )}
          <CardContent className={provisionOpen ? 'pt-0' : ''}>
            <div className="flex flex-wrap gap-2 pb-4">
              {['all', 'active', 'suspended'].map((f) => (
                <Button
                  key={f}
                  variant={filter === f ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                </Button>
              ))}
            </div>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : restaurants.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">No restaurants found.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border/60">
                <table className="w-full min-w-[1100px] text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-muted-foreground">
                      <th className="px-3 py-3 font-medium">Venue</th>
                      <th className="px-3 py-3 font-medium">Bookings</th>
                      <th className="px-3 py-3 font-medium">Emails</th>
                      <th className="px-3 py-3 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          <img src={brandUrl('google-calendar.svg')} alt="" className="h-5 w-5 rounded" />
                          Calendar
                        </span>
                      </th>
                      <th className="px-3 py-3 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          <img src={brandUrl('stripe-wordmark.svg')} alt="" className="h-4 w-auto" />
                          Stripe
                        </span>
                      </th>
                      <th className="px-3 py-3 font-medium">Users</th>
                      <th className="px-3 py-3 font-medium">Created</th>
                      <th className="px-3 py-3 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {restaurants.map((r) => (
                      <tr key={r.id} className="border-b border-border/50 last:border-0">
                        <td className="px-3 py-3 align-top">
                          <p className="font-medium">{r.name}</p>
                          <p className="text-xs text-muted-foreground">{r.email || '—'}</p>
                          <Badge variant={r.status === 'suspended' ? 'destructive' : 'secondary'} className="mt-1">
                            {r.status === 'suspended' ? 'Suspended' : 'Active'}
                          </Badge>
                          {r.billing?.subscription_status === 'active' && !r.billing?.stripe_subscription_id ? (
                            <Badge className="mt-1 ml-2 bg-emerald-600 text-white">
                              <ShieldCheck className="mr-1 h-3 w-3" />
                              Full Access
                            </Badge>
                          ) : r.billing?.subscription_status === 'trialing' ? (
                            <Badge variant="outline" className="mt-1 ml-2">
                              {(() => {
                                const ends = r.billing?.trial_ends_at || r.billing?.current_period_end;
                                if (!ends) return 'Trial';
                                try {
                                  const daysLeft = Math.max(0, Math.ceil((new Date(ends) - new Date()) / 86400000));
                                  return `Trial · ${daysLeft}d left`;
                                } catch { return 'Trial'; }
                              })()}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="mt-1 ml-2">
                              {r.billing?.subscription_status || 'inactive'}
                            </Badge>
                          )}
                          {r.billing?.platform_note && (
                            <p className="mt-1 text-[11px] text-muted-foreground italic">{r.billing.platform_note}</p>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top tabular-nums">{r.booking_count ?? 0}</td>
                        <td className="px-3 py-3 align-top tabular-nums">{r.emails_sent ?? 0}</td>
                        <td className="px-3 py-3 align-top">
                          {r.google_calendar_connected ? (
                            <Badge className="bg-emerald-600">Connected</Badge>
                          ) : (
                            <Badge variant="outline">Not connected</Badge>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <Badge variant={stripeStatusVariant(r.stripe)}>{stripeStatusLabel(r.stripe)}</Badge>
                          {r.stripe?.account_id ? (
                            <div className="mt-1.5 space-y-0.5 text-xs">
                              <p className="font-mono text-foreground">{r.stripe.account_id}</p>
                              {r.stripe.business_name && (
                                <p className="text-muted-foreground">{r.stripe.business_name}</p>
                              )}
                              {r.stripe.account_email && (
                                <p className="text-muted-foreground">{r.stripe.account_email}</p>
                              )}
                              {typeof r.stripe.payouts_enabled_live === 'boolean' && (
                                <p className="text-muted-foreground">
                                  Live payouts: {r.stripe.payouts_enabled_live ? 'enabled' : 'disabled'}
                                </p>
                              )}
                              {r.stripe.retrieve_error && (
                                <p className="text-amber-700">Stripe lookup: unavailable</p>
                              )}
                              {/* Live Stripe balance */}
                              {r.stripe.stripe_balance ? (
                                r.stripe.stripe_balance.error ? (
                                  <div className="mt-1.5 rounded bg-amber-50 border border-amber-200 px-2 py-1.5 text-xs text-amber-800">
                                    <p className="font-semibold">Stripe balance: unavailable</p>
                                    <p className="text-amber-700 mt-0.5">Money has likely already been paid to their bank — Stripe Express accounts restrict balance reads in some cases.</p>
                                  </div>
                                ) : (
                                  <div className="mt-1.5 rounded bg-slate-50 border border-slate-200 px-2 py-1.5 text-xs space-y-0.5">
                                    <p className="font-semibold text-slate-700">Stripe balance (live)</p>
                                    {(r.stripe.stripe_balance.available || []).map((b, i) => (
                                      <p key={i} className={b.amount > 0 ? 'text-emerald-700 font-medium' : 'text-slate-500'}>
                                        Available: {b.currency === 'GBP' ? '£' : b.currency}{b.amount.toFixed(2)}
                                        {b.amount === 0 && <span className="ml-1 text-slate-400">(paid out to bank)</span>}
                                      </p>
                                    ))}
                                    {(r.stripe.stripe_balance.pending || []).map((b, i) => (
                                      <p key={i} className={b.amount > 0 ? 'text-amber-700' : 'text-slate-500'}>
                                        Pending payout: {b.currency === 'GBP' ? '£' : b.currency}{b.amount.toFixed(2)}
                                      </p>
                                    ))}
                                  </div>
                                )
                              ) : null}
                              <p className="pt-1 text-muted-foreground">
                                Paid deposits: {formatGbp(r.stripe.paid_deposit_total)} ({r.stripe.paid_deposit_count || 0})
                              </p>
                              {r.stripe.pending_deposit_count > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => setUnpaidDepositsFor({ id: r.id, name: r.name })}
                                  className="text-left text-amber-700 underline decoration-dotted underline-offset-2 hover:text-amber-900"
                                  data-testid={`unpaid-deposits-${r.id}`}
                                >
                                  Pending (unpaid) deposits: {formatGbp(r.stripe.pending_deposit_total)} ({r.stripe.pending_deposit_count}) · view
                                </button>
                              ) : (
                                <p className="text-muted-foreground">Pending (unpaid) deposits: {formatGbp(0)} (0)</p>
                              )}
                              <p className="text-muted-foreground">
                                Refunded: {formatGbp(r.stripe.refunded_deposit_total)}
                              </p>
                              {r.stripe.last_payout ? (
                                <p className={`pt-1 font-medium ${r.stripe.last_payout.status === 'paid' ? 'text-emerald-700' : 'text-amber-700'}`}>
                                  Last payout: {r.stripe.last_payout.currency === 'GBP' ? '£' : r.stripe.last_payout.currency}{' '}
                                  {r.stripe.last_payout.amount.toFixed(2)} · {r.stripe.last_payout.status}
                                  {r.stripe.last_payout.arrival_date && (
                                    <> · {format(new Date(r.stripe.last_payout.arrival_date * 1000), 'dd MMM yyyy')}</>
                                  )}
                                </p>
                              ) : (
                                <p className="pt-1 text-xs text-slate-400 italic">Payout history not readable — money is in their Stripe balance or already in their bank</p>
                              )}
                            </div>
                          ) : (
                            <p className="mt-1 text-xs text-muted-foreground">No connected account</p>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top tabular-nums">{r.user_count}</td>
                        <td className="px-3 py-3 align-top text-muted-foreground">
                          {r.created_at ? format(new Date(r.created_at), 'MMM d, yyyy') : '—'}
                        </td>
                        <td className="px-3 py-3 align-top text-right">
                          <div className="flex flex-col items-end gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="gap-1.5"
                              disabled={!r.email || sendingStripeFor === r.id}
                              title={!r.email ? 'Add a company email on the tenant Profile first' : undefined}
                              onClick={() => sendStripeSetupEmail(r.id)}
                              data-testid={`platform-stripe-email-${r.id}`}
                            >
                              {sendingStripeFor === r.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Mail className="h-3.5 w-3.5" />
                              )}
                              Stripe email
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="gap-1.5"
                              disabled={sendingBillingFor === r.id}
                              onClick={() => startBillingCheckout(r.id)}
                              data-testid={`platform-billing-checkout-${r.id}`}
                            >
                              {sendingBillingFor === r.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <CreditCard className="h-3.5 w-3.5" />
                              )}
                              Billing checkout
                            </Button>
                            {/* Billing access override */}
                            {r.billing?.subscription_status !== 'active' || r.billing?.stripe_subscription_id ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => { setBillingOverrideDialog({ restaurant: r }); setBillingOverrideNote(''); }}
                              >
                                <ShieldCheck className="h-3.5 w-3.5" />
                                Grant access
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1.5 text-muted-foreground"
                                onClick={() => resetToTrial(r)}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                                Reset to trial
                              </Button>
                            )}
                            {r.status === 'suspended' ? (
                              <Button size="sm" variant="outline" onClick={() => setStatus(r.id, 'active')}>
                                Activate
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-destructive/30 text-destructive hover:bg-destructive/10"
                                onClick={() => setStatus(r.id, 'suspended')}
                              >
                                Suspend
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Grant full access dialog */}
      <Dialog open={!!billingOverrideDialog} onOpenChange={(open) => { if (!open) setBillingOverrideDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              Grant full access
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{billingOverrideDialog?.restaurant?.name}</span> will be marked
              as fully active — no trial banner or countdown will be shown to their team.
            </p>
            <div className="space-y-2">
              <Label htmlFor="billing-note">Note (optional)</Label>
              <Textarea
                id="billing-note"
                placeholder="e.g. Paid annually via bank transfer — Jan 2026"
                value={billingOverrideNote}
                onChange={(e) => setBillingOverrideNote(e.target.value)}
                rows={2}
              />
              <p className="text-xs text-muted-foreground">This note is visible only to you in the platform dashboard.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillingOverrideDialog(null)}>Cancel</Button>
            <Button
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={grantFullAccess}
              disabled={savingBillingOverride}
            >
              {savingBillingOverride ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Grant full access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UnpaidDepositsDialog
        restaurant={unpaidDepositsFor}
        onOpenChange={(open) => { if (!open) setUnpaidDepositsFor(null); }}
      />
    </div>
  );
}
